/**
 * SprintRunSelector — Horizontal pill bar showing all build attempts (runs)
 * for a sprint, with run detail card and previous attempts accordion.
 *
 * Renders:
 * 1. Pill bar: #1, #2, #3... with status color/icon
 * 2. Selected run detail card: status, duration, failure reason, test summary, PR link
 * 3. Previous Attempts accordion below the telemetry
 */

import { useState, useMemo, useEffect } from 'react';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  History,
} from 'lucide-react';
import type { SprintRun } from '../../api/sprint';
import { useSprintRuns } from '../../hooks/useSprintData';
import { formatDuration, formatBuildDate } from '../../utils/dates';

/* ---- Status helpers ---------------------------------------------------- */

type RunStatus = 'completed' | 'failed' | 'partial' | 'building' | string;

function classifyStatus(run: SprintRun): RunStatus {
  if (run.status === 'completed') return 'completed';
  if (run.status === 'failed') return 'failed';
  if (run.status === 'building' || run.status === 'running') return 'building';
  if (run.storiesCompleted > 0 && run.storiesFailed > 0) return 'partial';
  return run.status;
}

const STATUS_CONFIG: Record<string, { bg: string; text: string; border: string; dot: string; label: string }> = {
  completed: { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', label: 'Completed' },
  failed:    { bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-200',     dot: 'bg-red-500',     label: 'Failed' },
  partial:   { bg: 'bg-amber-50',    text: 'text-amber-700',   border: 'border-amber-200',   dot: 'bg-amber-500',   label: 'Partial' },
  building:  { bg: 'bg-blue-50',     text: 'text-blue-700',    border: 'border-blue-200',     dot: 'bg-blue-500',    label: 'Building' },
  planning:  { bg: 'bg-neutral-50',  text: 'text-neutral-600', border: 'border-neutral-200',  dot: 'bg-neutral-400', label: 'Planning' },
};

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] || STATUS_CONFIG.planning;
}

function StatusIcon({ status, size = 14 }: { status: string; size?: number }) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 size={size} className="text-emerald-500" />;
    case 'failed':
      return <XCircle size={size} className="text-red-500" />;
    case 'building':
      return <Loader2 size={size} className="text-blue-500 animate-spin" />;
    case 'partial':
      return <AlertTriangle size={size} className="text-amber-500" />;
    default:
      return <Clock size={size} className="text-neutral-400" />;
  }
}

/* ---- Pill bar ---------------------------------------------------------- */

function RunPill({
  run,
  isSelected,
  onClick,
}: {
  run: SprintRun;
  isSelected: boolean;
  onClick: () => void;
}) {
  const status = classifyStatus(run);
  const cfg = getStatusConfig(status);

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all border
        ${isSelected
          ? `${cfg.bg} ${cfg.text} ${cfg.border} shadow-sm`
          : 'bg-white text-neutral-500 border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
        }`}
      title={`Attempt #${run.attemptNumber} - ${cfg.label}`}
    >
      <StatusIcon status={status} size={12} />
      <span>#{run.attemptNumber}</span>
    </button>
  );
}

/* ---- Run detail card --------------------------------------------------- */

function RunDetailCard({ run }: { run: SprintRun }) {
  const status = classifyStatus(run);
  const cfg = getStatusConfig(status);
  const duration = formatDuration(run.startedAt ?? undefined, run.completedAt || run.failedAt || undefined);
  const totalStories = run.storiesTotal || (run.storiesCompleted + run.storiesFailed);

  return (
    <div className={`rounded-lg border ${cfg.border} ${cfg.bg} px-4 py-3`}>
      <div className="flex items-center gap-3 flex-wrap">
        {/* Status badge */}
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${cfg.text} ${cfg.bg} border ${cfg.border}`}>
          <StatusIcon status={status} size={11} />
          {cfg.label}
        </span>

        {/* Attempt number */}
        <span className="text-[11px] font-mono text-neutral-500">
          Attempt #{run.attemptNumber}
        </span>

        {/* Duration */}
        {duration && (
          <span className="text-[11px] text-neutral-500 flex items-center gap-1">
            <Clock size={10} />
            {duration}
          </span>
        )}

        {/* Stories progress */}
        {totalStories > 0 && (
          <span className="text-[11px] font-mono text-neutral-500">
            {run.storiesCompleted}/{totalStories} stories
            {run.storiesFailed > 0 && (
              <span className="text-red-500 ml-1">({run.storiesFailed} failed)</span>
            )}
          </span>
        )}

        {/* Timing */}
        {run.startedAt && (
          <span className="text-[10px] text-neutral-400 ml-auto">
            {formatBuildDate(run.startedAt)}
          </span>
        )}
      </div>

      {/* Failure reason */}
      {run.failureReason && (
        <div className="mt-2.5 rounded border border-red-200 bg-red-50/50 px-3 py-2">
          <p className="text-[11px] font-semibold text-red-700 mb-0.5">Failure Reason</p>
          <p className="text-[11px] text-red-600 leading-relaxed">{run.failureReason}</p>
        </div>
      )}
    </div>
  );
}

/* ---- Previous Attempts accordion --------------------------------------- */

function PreviousAttemptsAccordion({
  runs,
  currentRunId,
  onSelectRun,
}: {
  runs: SprintRun[];
  currentRunId: string;
  onSelectRun: (run: SprintRun) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const previousRuns = runs.filter(r => r.id !== currentRunId);

  if (previousRuns.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-neutral-200 shadow-warm-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-neutral-50 transition-colors"
      >
        <History size={14} className="text-neutral-400 shrink-0" />
        <span className="text-[12px] font-semibold text-neutral-600">
          Previous Attempts
        </span>
        <span className="text-[10px] font-mono text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded">
          {previousRuns.length}
        </span>
        <span className="ml-auto">
          {isOpen
            ? <ChevronDown size={14} className="text-neutral-400" />
            : <ChevronRight size={14} className="text-neutral-400" />
          }
        </span>
      </button>

      {isOpen && (
        <div className="border-t border-neutral-100 divide-y divide-neutral-100">
          {previousRuns.map(run => {
            const status = classifyStatus(run);
            const cfg = getStatusConfig(status);
            const duration = formatDuration(run.startedAt ?? undefined, run.completedAt || run.failedAt || undefined);
            const totalStories = run.storiesTotal || (run.storiesCompleted + run.storiesFailed);

            return (
              <div key={run.id} className="px-4 py-3 hover:bg-neutral-50/50 transition-colors">
                <div className="flex items-center gap-3">
                  {/* Status icon + attempt number */}
                  <StatusIcon status={status} size={14} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[12px] font-semibold text-neutral-700">
                        Attempt #{run.attemptNumber}
                      </span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cfg.text} ${cfg.bg}`}>
                        {cfg.label}
                      </span>
                      {duration && (
                        <span className="text-[10px] text-neutral-400 flex items-center gap-0.5">
                          <Clock size={9} />
                          {duration}
                        </span>
                      )}
                      {totalStories > 0 && (
                        <span className="text-[10px] font-mono text-neutral-400">
                          {run.storiesCompleted}/{totalStories} stories
                        </span>
                      )}
                    </div>
                    {run.failureReason && (
                      <p className="text-[10px] text-red-500 mt-1 truncate">{run.failureReason}</p>
                    )}
                    {run.startedAt && (
                      <p className="text-[10px] text-neutral-400 mt-0.5">{formatBuildDate(run.startedAt)}</p>
                    )}
                  </div>

                  {/* View telemetry button */}
                  <button
                    onClick={() => onSelectRun(run)}
                    className="shrink-0 text-[11px] font-medium text-coral-500 hover:text-coral-600 transition-colors px-2 py-1 rounded hover:bg-coral-50"
                  >
                    View telemetry
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---- Main component ---------------------------------------------------- */

interface SprintRunSelectorProps {
  sprintId: string | undefined;
  /** Called when the selected run changes. Emits batchId for the run. */
  onRunChange?: (batchId: string | null, run: SprintRun | null) => void;
}

export function SprintRunSelector({ sprintId, onRunChange }: SprintRunSelectorProps) {
  const { data, isLoading } = useSprintRuns(sprintId);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const runs = useMemo(() => data?.runs ?? [], [data]);

  // Resolve selected run: explicit selection > most recent (first in array, descending order)
  const selectedRun = useMemo(() => {
    if (runs.length === 0) return null;
    if (selectedRunId) {
      const found = runs.find(r => r.id === selectedRunId);
      if (found) return found;
    }
    return runs[0]; // most recent (ordered by attempt_number DESC from API)
  }, [runs, selectedRunId]);

  const handleSelectRun = (run: SprintRun) => {
    setSelectedRunId(run.id);
    onRunChange?.(run.batchId, run);
  };

  // Notify parent of initial/changed selection
  useEffect(() => {
    if (selectedRun) {
      onRunChange?.(selectedRun.batchId, selectedRun);
    }
  }, [selectedRun?.id]);

  // Don't render if no runs or loading
  if (isLoading || runs.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Pill bar — only show if multiple runs */}
      {runs.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 mr-1">
            Runs
          </span>
          {/* Show pills in ascending order (oldest first) */}
          {[...runs].reverse().map(run => (
            <RunPill
              key={run.id}
              run={run}
              isSelected={run.id === selectedRun?.id}
              onClick={() => handleSelectRun(run)}
            />
          ))}
        </div>
      )}

      {/* Selected run detail card */}
      {selectedRun && <RunDetailCard run={selectedRun} />}
    </div>
  );
}

/* ---- Exported sub-component for parent layout -------------------------- */

export { PreviousAttemptsAccordion };
export type { SprintRun as SprintRunType };
